import { CORE, LARVA, LARVA_DOOR_ACCESS, LARVA_DOOR_ACCESS_TEMP, LARVA_WS } from 'core/datasets/action';
import { AppState } from 'core/redux/rootReducer';
import { AnyAction, Middleware } from 'redux';
import { larvaDoorAccessActions } from '../actions/larvaDoorAccessActions';
import { ResponseStatus } from 'core/classes/ResponseStatus';
import { handleErrorResponse } from 'core/redux/helpers/coreReduxHelpers';
import {
    fetchLarvaDoorAccess,
    generateInfoNotification,
    generateLarvaConfigFromState,
    generateSuccessNotification,
} from './helpers/larvaMiddlewareHelpers';
import LarvaConnectionConfig from 'smartHome/larva/classes/LarvaConnectionConfig';
import DoorAccess from 'smartHome/larva/classes/DoorAccess';
import DoorAccessType from 'smartHome/larva/classes/DoorAccessType';
import {
    createAccess,
    deleteAccess,
    sendPinCodeByEmail,
    sendPinCodeBySMS,
    unsetAccessPin,
    updateAccessData,
    updateAccessPin,
} from 'smartHome/larva/api/larvaDoorAccessApi';
import { coreActions } from 'core/redux/actions/coreActions';
import { larvaSelectors } from '../larvaReducer';
import { settingsSelectors } from 'core/redux/selectors/settingsSelectors';
import { throttledCallGenerator } from './helpers/throttledCallGenerator';
import { ITokenService } from 'core/config/gql/TokenService';

const NOTIFICATIONS = {
    doorAccessDeleted: 'smartHome.larva.doorAccess.notifications.doorAccessDeleted',
    pinCodeUpdated: 'smartHome.larva.doorAccess.notifications.pinCodeUpdated',
    pinCodeUnset: 'smartHome.larva.doorAccess.notifications.pinCodeUnset',
    pinCodeSent: 'smartHome.larva.doorAccess.notifications.pinCodeSent',
    doorAccessDataUpdated: 'smartHome.larva.doorAccess.notifications.doorAccessDataUpdated',
    doorAccessCreated: 'smartHome.larva.doorAccess.notifications.doorAccessCreated',
};

interface LarvaDoorkAccessMiddlewareDeps {
    tokenService: ITokenService;
}

function createLarvaWatchDoorAccessMiddleware(deps: LarvaDoorkAccessMiddlewareDeps): Middleware {
    const { tokenService } = deps;

    return ({ getState, dispatch }) =>
        (next) =>
        async (action: AnyAction) => {
            next(action);

            const state: AppState = getState();

            if (!tokenService) return;

            await tokenService.refreshToken();
            const connectionConfig = generateLarvaConfigFromState(state, tokenService);
            if (!connectionConfig) {
                return;
            }

            const dispatchDoorAccessFetch = () =>
                dispatch({
                    type: LARVA.API_DOOR_ACCESS_FETCH_STARTED,
                    // This is error, not supposed to pass classes to actions payload
                    payload: new LarvaConnectionConfig(connectionConfig),
                });

            const throttle15000 = throttledCallGenerator(15000);

            switch (action.type) {
                case LARVA_DOOR_ACCESS.SIGNALS.REFRESH_DOOR_ACCESS: {
                    dispatch({
                        type: LARVA.API_DOOR_ACCESS_FETCH_STARTED,
                        // This is an error, not supposed to pass classes to actions payload
                        payload: new LarvaConnectionConfig(connectionConfig),
                    });
                    break;
                }

                case LARVA_DOOR_ACCESS.DOOR_ACCESS_COMMAND.DOOR_ACCESS_SEND_SMS_ASYNC: {
                    const access = action.payload;

                    try {
                        if (!DoorAccessType.isCode(access.accessType)) {
                            throw new Error('Invalid access type');
                        }

                        const response = await sendPinCodeBySMS(access, connectionConfig);

                        if (response && ResponseStatus.isOk(response.status)) {
                            dispatch(larvaDoorAccessActions.signal.pinCodeSentBySMS());
                            dispatch(generateSuccessNotification(NOTIFICATIONS.pinCodeSent));
                            break;
                        }

                        throw new Error('Failed to update new access');
                    } catch (reason) {
                        console.error('Failed to send SMS', reason);
                        handleErrorResponse(dispatch, reason, action);
                    }

                    break;
                }

                case LARVA_DOOR_ACCESS.DOOR_ACCESS_COMMAND.DOOR_ACCESS_SEND_EMAIL_ASYNC: {
                    try {
                        const access = action.payload;

                        if (!DoorAccessType.isCode(access.accessType)) {
                            break;
                        }

                        const response = await sendPinCodeByEmail(access, connectionConfig);

                        if (response && ResponseStatus.isOk(response.status)) {
                            dispatch(larvaDoorAccessActions.signal.pinCodeSentByEmail());
                            dispatch(generateSuccessNotification(NOTIFICATIONS.pinCodeSent));
                            break;
                        }
                        console.log('Failed to update new access: ', response);
                    } catch (reason) {
                        console.error('Failed to send by Email', reason);
                        handleErrorResponse(dispatch, reason, action);
                    }

                    break;
                }

                case LARVA_DOOR_ACCESS.DOOR_ACCESS_COMMAND.DOOR_ACCESS_UNSET_PIN_ASYNC: {
                    try {
                        const access = action.payload;
                        const response = await unsetAccessPin(access, connectionConfig);

                        if (response && ResponseStatus.isOk(response.status)) {
                            dispatchDoorAccessFetch();
                            dispatch(larvaDoorAccessActions.selectDoorAccessById(access.id));
                            dispatch(larvaDoorAccessActions.signal.doorAccessPinUnset());
                            dispatch(generateInfoNotification(NOTIFICATIONS.pinCodeUnset));
                        } else {
                            console.log('Failed to update new access: ', response);
                        }
                    } catch (reason) {
                        console.error('Failed to unset the door access pin', { reason });
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                case LARVA_DOOR_ACCESS.DOOR_ACCESS_COMMAND.DOOR_ACCESS_UPDATE_PIN_ASYNC: {
                    try {
                        const access = action.payload;
                        const response = await updateAccessPin(action.payload, connectionConfig);

                        if (response && ResponseStatus.isOk(response.status)) {
                            const newAccess = new DoorAccess({
                                ...response.data,
                                accessType: access.accessType,
                            });
                            dispatchDoorAccessFetch();
                            dispatch(larvaDoorAccessActions.selectDoorAccess(newAccess.data));
                            dispatch(larvaDoorAccessActions.signal.doorAccessPinUpdated());
                            dispatch(generateSuccessNotification(NOTIFICATIONS.pinCodeUpdated));
                        }
                    } catch (reason) {
                        console.error('Failed to update the door access pin', { reason });
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                case LARVA_DOOR_ACCESS.DOOR_ACCESS_FORM_COMMANDS.CREATE_DOOR_ACCESS: {
                    try {
                        const { access, navigate } = action.payload;
                        const accessId = access.id;

                        const response = await createAccess(access, connectionConfig);

                        if (ResponseStatus.isOk(response.status)) {
                            const newAccess = new DoorAccess({
                                ...response.data,
                                accessType: access.accessType,
                            });

                            dispatchDoorAccessFetch();
                            dispatch(larvaDoorAccessActions.signal.newDoorAccessCreated());
                            dispatch(larvaDoorAccessActions.selectDoorAccess(newAccess.data));
                            navigate(`/door-access/${accessId}`);
                            dispatch(coreActions.slidePanel.changeOpenState());
                            dispatch(generateSuccessNotification(NOTIFICATIONS.doorAccessCreated));
                            break;
                        }

                        console.log('Cannot create new access: ', response);
                    } catch (reason) {
                        console.error('Failed to update the door access pin', { reason });
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                case LARVA_DOOR_ACCESS.DOOR_ACCESS_COMMAND.DOOR_ACCESS_DELETE_ASYNC: {
                    try {
                        const { access, navigate } = action.payload;
                        await deleteAccess(access, connectionConfig);

                        dispatchDoorAccessFetch();
                        dispatch(larvaDoorAccessActions.signal.doorAccessDeleted());
                        dispatch(larvaDoorAccessActions.showInChildFirstAvailableDoorAccess(navigate));
                        dispatch(generateSuccessNotification(NOTIFICATIONS.doorAccessDeleted));
                    } catch (reason) {
                        console.error('Failed to delete the door access', { reason });
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                case LARVA_DOOR_ACCESS.DOOR_ACCESS_COMMAND.DOOR_ACCESS_UPDATE_USER_DATA: {
                    try {
                        const { access, data } = action.payload;
                        const response = await updateAccessData(access, data, connectionConfig);

                        if (ResponseStatus.isOk(response.status)) {
                            const newAccess = new DoorAccess({
                                ...response.data,
                                accessType: access.accessType,
                            });

                            dispatch({ type: LARVA.API_DOOR_ACCESS_FETCH_STARTED, payload: connectionConfig });
                            dispatch(larvaDoorAccessActions.selectDoorAccess(newAccess.data));
                            dispatch(larvaDoorAccessActions.signal.doorAccessPinUpdated());
                            dispatch(generateSuccessNotification(NOTIFICATIONS.doorAccessDataUpdated));
                            break;
                        }

                        console.log('Cannot update the access: ', response);
                    } catch (reason) {
                        console.error('Failed to update the access: ', reason);
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                case LARVA_DOOR_ACCESS_TEMP.DOOR_ACCESS_LIST_VIEW_DATA_ITEM_SELECTED: {
                    try {
                        const id = action.payload;
                        const doorAccessMap = larvaSelectors.doorAccessMap(state);
                        const doorAccess = doorAccessMap[id] || null;

                        if (!doorAccess) {
                            console.error('Door access Id not found', [doorAccessMap, id]);
                            return;
                        }

                        dispatch(larvaDoorAccessActions.selectDoorAccess(doorAccess));
                        break;
                    } catch (reason) {
                        console.error('Failed to update the access: ', reason);
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                case LARVA_DOOR_ACCESS_TEMP.DOOR_ACCESS_SELECTED_BY_ID: {
                    try {
                        const id = action.payload;
                        const doorAccessMap = larvaSelectors.doorAccessMap(state);
                        const doorAccess = doorAccessMap[id] || null;

                        if (!doorAccess) {
                            console.error('Door access Id not found', [doorAccessMap, id]);
                            return;
                        }

                        dispatch(larvaDoorAccessActions.selectDoorAccess(doorAccess));
                        break;
                    } catch (reason) {
                        console.error('Failed to update the access: ', reason);
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                case LARVA_DOOR_ACCESS_TEMP.SHOW_FIRST_AVAILABLE_DOOR_ACCESS: {
                    try {
                        const navigate = action.payload;
                        const doorAccessMap = larvaSelectors.doorAccessMap(state);
                        const isFullViewMode = settingsSelectors.isFullView(state);
                        const doorAccess = Object.values(doorAccessMap)[0];

                        if (doorAccess) {
                            const url = `/door-access/${isFullViewMode ? doorAccess.id : ''}`;

                            dispatch(larvaDoorAccessActions.selectDoorAccessListViewItem(doorAccess.id));

                            //@ts-ignore
                            navigate(url);
                        }
                    } catch (reason) {
                        console.error('Failed to update the access: ', reason);
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                case LARVA.API_DOOR_ACCESS_FETCH_STARTED: {
                    try {
                        fetchLarvaDoorAccess(connectionConfig, state, dispatch);
                    } catch (reason) {
                        console.error('Failed execute door access: ', reason);
                        handleErrorResponse(dispatch, reason, action);
                    }
                    break;
                }

                // Refetch door access on with throttle (as was in saga)
                case LARVA_WS.WS_CONNECTION_ERROR: {
                    throttle15000(() => {
                        fetchLarvaDoorAccess(connectionConfig, state, dispatch);
                    });
                    break;
                }

                case CORE.APP_ACTIVE_STATUS_CHANGED: {
                    throttle15000(() => {
                        fetchLarvaDoorAccess(connectionConfig, state, dispatch);
                    });
                    break;
                }

                case LARVA.PUT_API_UNITS: {
                    throttle15000(() => {
                        fetchLarvaDoorAccess(connectionConfig, state, dispatch);
                    });
                    break;
                }

                case LARVA.PUT_UNIT_DEVICES_MAP: {
                    throttle15000(() => {
                        fetchLarvaDoorAccess(connectionConfig, state, dispatch);
                    });
                    break;
                }

                case LARVA_WS.TRY_UPDATE_WS_CONNECTIONS: {
                    throttle15000(() => {
                        fetchLarvaDoorAccess(connectionConfig, state, dispatch);
                    });
                    break;
                }

                case LARVA_WS.REQUEST_ERROR: {
                    throttle15000(() => {
                        fetchLarvaDoorAccess(connectionConfig, state, dispatch);
                    });
                    break;
                }

                default:
                    break;
            }
        };
}

export default createLarvaWatchDoorAccessMiddleware;
